/**
 * iter301 — Comptabilité → onglet TVA (décompte TDFN).
 *
 * Présentation demandée : CA total − CA non soumis = CA déterminant, puis une ligne
 * par taux TDFN de la période. L'impôt préalable est affiché sans jamais être déduit.
 * Un décompte clôturé affiche ses valeurs figées.
 */
import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Download, Lock, Unlock, AlertTriangle, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import TdfnRegularisationCard from "@/components/compta/TdfnRegularisationCard";
import IpReclassCard from "@/components/compta/IpReclassCard";
import TvaMethodeCard from "@/components/parametres/TvaMethodeCard";
import TdfnEncaissementsCard from "@/components/compta/TdfnEncaissementsCard";

const fmt = (n) => (n || 0).toLocaleString("fr-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join(".") : "—");

const TvaDecompteTab = ({ role }) => {
  const today = new Date();
  const Q = Math.floor(today.getMonth() / 3) + 1;
  const [from, setFrom] = useState(new Date(today.getFullYear(), (Q - 1) * 3, 1).toISOString().slice(0, 10));
  const [to, setTo] = useState(new Date(today.getFullYear(), Q * 3, 0).toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [clotures, setClotures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const [d, c] = await Promise.all([
        api.get("/tva/decompte", { params: { date_from: from, date_to: to } }),
        api.get("/tva/decomptes"),
      ]);
      setData(d.data);
      setClotures(c.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Chargement impossible");
    } finally { setLoading(false); }
  }, [from, to]);
  useEffect(() => { charger(); }, [charger]);

  const setQuarter = (year, q) => {
    setFrom(new Date(year, (q - 1) * 3, 1).toISOString().slice(0, 10));
    setTo(new Date(year, q * 3, 0).toISOString().slice(0, 10));
  };

  const downloadPdf = () => {
    const token = localStorage.getItem("cm_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/journal/tva-decompte/pdf?date_from=${from}&date_to=${to}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => r.blob()).then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `decompte-tva-${from}-${to}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    }).catch(() => toast.error("Erreur PDF"));
  };

  const cloturer = async () => {
    if (!window.confirm(
      `Clôturer le décompte du ${fmtDate(from)} au ${fmtDate(to)} ?\n\n`
      + `TVA due : ${fmt(data.tva_due_total)} CHF\n\n`
      + "Les valeurs sont figées : aucune écriture ni aucun changement de taux ne pourra "
      + "plus les modifier.",
    )) return;
    setBusy(true);
    try {
      await api.post("/tva/decomptes/cloturer", { date_from: from, date_to: to });
      toast.success("Décompte clôturé et figé");
      charger();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const rouvrir = async (d) => {
    if (!window.confirm(`Rouvrir le décompte ${fmtDate(d.periode.from)} → ${fmtDate(d.periode.to)} ?\n\nIl redeviendra recalculé en direct.`)) return;
    try {
      await api.delete(`/tva/decomptes/${d.id}?confirmer=true`);
      toast.success("Décompte réouvert");
      charger();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const year = today.getFullYear();
  const effective = data?.methode === "effective";

  return (
    <div data-testid="tva-tab" className="space-y-4">
      <Card className="p-4 border-gray-200">
        <div className="flex items-center gap-2 flex-wrap">
          <Label className="text-xs uppercase text-gray-500">Période :</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" data-testid="tva-from" />
          <span className="text-gray-400">→</span>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" data-testid="tva-to" />
          <div className="flex gap-1 ml-2 flex-wrap">
            {[1, 2, 3, 4].map((q) => (
              <Button key={q} size="sm" variant="outline" onClick={() => setQuarter(year, q)}>Q{q} {year}</Button>
            ))}
            <Button size="sm" variant="outline" onClick={() => setQuarter(year - 1, 4)}>Q4 {year - 1}</Button>
          </div>
          <Button onClick={downloadPdf} className="ml-auto bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="tva-pdf-btn">
            <Download className="w-4 h-4 mr-2" /> Export PDF
          </Button>
        </div>
      </Card>

      {/* iter338 — Déplacée depuis Réglages : la méthode de décompte se règle ici */}
      {(role === "admin" || role === "compta") && <TvaMethodeCard />}

      {loading && <div className="text-center py-12 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}

      {!loading && effective && (
        <Card className="p-4 border-amber-200 bg-amber-50/50 text-sm text-amber-900" data-testid="tva-methode-effective">
          Méthode effective active. Pour passer au taux de la dette fiscale nette, utilise la
          carte « TVA — méthode de décompte » ci-dessus.
        </Card>
      )}

      {!loading && data && !effective && (
        <>
          {data.cloture && (
            <Card className="p-3 border-gray-300 bg-gray-50 flex items-center gap-2 text-sm" data-testid="tva-decompte-cloture">
              <Lock className="w-4 h-4 text-gray-600" />
              <span>
                Décompte clôturé le <b>{fmtDate(data.cloture_le)}</b>
                {data.cloture_par ? ` par ${data.cloture_par}` : ""} — valeurs figées.
              </span>
            </Card>
          )}

          {(data.avertissements || []).map((a) => (
            <Card key={a} className="p-3 border-amber-300 bg-amber-50 flex items-start gap-2 text-xs text-amber-900" data-testid="tva-avertissement">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{a}</span>
            </Card>
          ))}

          <Card className="border-gray-200 overflow-hidden">
            <div className="p-3 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-700 uppercase">
              Chiffre d&apos;affaires déterminant (TVA incluse)
            </div>
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b border-gray-100">
                  <td className="px-3 py-2 font-mono text-xs text-gray-400 w-16">200</td>
                  <td className="px-3 py-2 text-gray-700">Chiffre d&apos;affaires total</td>
                  <td className="px-3 py-2 text-right font-mono" data-testid="tva-ca-total">{fmt(data.ca_total_ttc)}</td>
                </tr>
                {data.deductions.map((d) => (
                  <tr key={d.code} className="border-b border-gray-100">
                    <td className="px-3 py-2 font-mono text-xs text-gray-400">221</td>
                    <td className="px-3 py-2 text-gray-600">− {d.libelle} ({d.code})</td>
                    <td className="px-3 py-2 text-right font-mono text-gray-600">− {fmt(d.montant)}</td>
                  </tr>
                ))}
                {data.total_a_classer > 0 && (
                  <tr className="border-b border-gray-100 bg-amber-50/40">
                    <td className="px-3 py-2 font-mono text-xs text-gray-400">—</td>
                    <td className="px-3 py-2 text-amber-900">− CA sans code TVA exploitable (à recoder)</td>
                    <td className="px-3 py-2 text-right font-mono text-amber-900" data-testid="tva-a-classer">− {fmt(data.total_a_classer)}</td>
                  </tr>
                )}
                <tr className="bg-[#52a675]/10 font-semibold">
                  <td className="px-3 py-2 font-mono text-xs text-gray-500">299</td>
                  <td className="px-3 py-2 text-gray-900">CA déterminant pour le TDFN</td>
                  <td className="px-3 py-2 text-right font-mono text-[#2d5f3f]" data-testid="tva-ca-determinant">{fmt(data.ca_determinant_ttc)}</td>
                </tr>
              </tbody>
            </table>
          </Card>

          <Card className="border-gray-200 overflow-hidden">
            <div className="p-3 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-700 uppercase">
              Impôt dû selon le taux de la dette fiscale nette
            </div>
            <table className="w-full text-sm">
              <tbody>
                {data.tranches.length === 0 && (
                  <tr><td className="px-3 py-4 text-center text-gray-400" colSpan={3}>Aucun chiffre d&apos;affaires soumis sur la période</td></tr>
                )}
                {data.tranches.map((t) => (
                  <tr key={t.date_debut} className="border-b border-gray-100" data-testid={`tva-tranche-${t.taux}`}>
                    <td className="px-3 py-2 font-mono text-xs text-gray-400 w-16">322</td>
                    <td className="px-3 py-2 text-gray-700">
                      {fmt(t.ca_ttc)} × <b>{t.taux.toFixed(2)} %</b>
                      <span className="text-xs text-gray-400 ml-2">
                        (dès le {fmtDate(t.date_debut)}{t.date_fin ? ` jusqu'au ${fmtDate(t.date_fin)}` : ""})
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(t.tva_due)}</td>
                  </tr>
                ))}
                <tr className="bg-red-50 font-bold">
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2 text-gray-900">TVA due à l&apos;AFC</td>
                  <td className="px-3 py-2 text-right font-mono text-red-800" data-testid="tva-due-total">{fmt(data.tva_due_total)}</td>
                </tr>
              </tbody>
            </table>
            <div className="p-3 text-[11px] text-gray-500 border-t border-gray-100">
              Impôt préalable de la période : {fmt(data.impot_preable_info)} CHF —
              <b> non déductible</b> avec cette méthode, indiqué pour information uniquement.
              {data.hors_champ ? ` · Produits hors champ exclus : ${fmt(data.hors_champ)} CHF.` : ""}
            </div>
          </Card>

          {!data.cloture && (role === "admin" || role === "compta") && (
            <Button onClick={cloturer} disabled={busy} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="tva-cloturer-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Clôturer ce décompte
            </Button>
          )}

          <TdfnRegularisationCard role={role} />
          <TdfnEncaissementsCard role={role} />
          <IpReclassCard role={role} />

          {clotures.length > 0 && (
            <Card className="border-gray-200 overflow-hidden" data-testid="tva-clotures">
              <div className="p-3 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-700 uppercase">
                Décomptes clôturés
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {clotures.map((c) => (
                    <tr key={c.id} className="border-t border-gray-100">
                      <td className="px-3 py-2 font-mono text-xs">{fmtDate(c.periode.from)} → {fmtDate(c.periode.to)}</td>
                      <td className="px-3 py-2 text-gray-600 text-xs">
                        CA {fmt(c.ca_determinant_ttc)} · {(c.taux_appliques || []).map((t) => `${t.taux.toFixed(2)} %`).join(" + ") || "—"}
                      </td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">{fmt(c.tva_due_total)}</td>
                      <td className="px-3 py-2 text-xs text-gray-400">clôturé le {fmtDate(c.cloture_le)}</td>
                      <td className="px-3 py-2 text-right">
                        {role === "admin" && (
                          <button type="button" className="p-1 text-gray-400 hover:text-amber-700" onClick={() => rouvrir(c)} title="Rouvrir" data-testid={`tva-rouvrir-${c.id}`}>
                            <Unlock className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default TvaDecompteTab;
